import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Zap, Loader2, Star } from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

interface Plan {
  id: string;
  name: string;
  price: number;
  features: string[];
  limits: Record<string, any>;
}

const PLAN_META: Record<string, {
  color: string;
  badge?: string;
  highlight?: boolean;
  cta: string;
  description: string;
}> = {
  Free: {
    color: '#6366F1',
    badge: 'Early Access',
    cta: 'Get Started',
    description: 'Full product, free during early access — all features unlocked',
  },
  Starter: {
    color: '#3B82F6',
    cta: 'Choose Plan',
    description: 'For small teams growing their pipeline',
  },
  Growth: {
    color: '#6366F1',
    badge: 'Most Popular',
    highlight: true,
    cta: 'Choose Plan',
    description: 'For growing businesses with full features',
  },
  Enterprise: {
    color: '#0F172A',
    cta: 'Contact Sales',
    description: 'Unlimited scale with dedicated support',
  },
};

export function ChoosePlanPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [selecting, setSelecting] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [cycle, setCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [annualDiscount, setAnnualDiscount] = useState(0);

  useEffect(() => {
    api.get('/plans')
      .then(r => setPlans(r.data))
      .catch(() => setError('Failed to load plans. Please refresh.'))
      .finally(() => setLoading(false));
    api.get('/payments/config')
      .then(({ data }) => setAnnualDiscount(Number(data?.annualDiscountPercent) || 0))
      .catch(() => {});
  }, []);

  // Amount for the selected cycle. Yearly = 12 months minus the annual discount.
  const cyclePrice = (monthly: number) =>
    cycle === 'yearly' ? Math.round(monthly * 12 * (1 - annualDiscount / 100)) : monthly;

  const handleSelect = async (plan: Plan) => {
    if (!user) return;
    setSelecting(plan.id);
    setError('');

    try {
      if (plan.price === 0) {
        // Free plan → activate immediately → dashboard
        await api.post('/subscriptions/select-free', { planId: plan.id });
        navigate('/onboarding', { replace: true });
      } else if (plan.name === 'Enterprise') {
        // Enterprise → contact sales (open mailto or navigate to contact)
        window.location.href = 'mailto:sales@brixlead.com?subject=Enterprise Plan Inquiry';
      } else {
        // Paid plan → go to payment page
        navigate(`/payment?planId=${plan.id}&planName=${encodeURIComponent(plan.name)}&price=${cyclePrice(plan.price)}&cycle=${cycle}`);
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setSelecting(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#6366F1]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Header */}
      <div className="text-center pt-14 pb-10 px-4">
        <div className="inline-flex items-center gap-2 bg-indigo-50 text-[#6366F1] text-xs font-semibold px-3 py-1.5 rounded-full mb-4">
          <Zap className="h-3.5 w-3.5" />
          Step 2 of 2
        </div>
        <h1 className="text-4xl font-bold text-slate-900 mb-3">Choose your plan</h1>
        <p className="text-slate-500 text-base max-w-xl mx-auto">
          Start free, upgrade anytime. No credit card required for Free plan.
        </p>

        {/* Billing cycle toggle */}
        <div className="mt-8 inline-flex items-center bg-white border border-gray-200 rounded-full p-1 shadow-sm">
          {(['monthly', 'yearly'] as const).map(c => (
            <button
              key={c}
              onClick={() => setCycle(c)}
              className={`relative px-5 py-1.5 text-sm font-semibold rounded-full transition-all capitalize ${
                cycle === c ? 'bg-[#6366F1] text-white shadow' : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              {c}
              {c === 'yearly' && annualDiscount > 0 && (
                <span className={`ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full font-bold ${cycle === 'yearly' ? 'bg-white/20 text-white' : 'bg-emerald-100 text-emerald-700'}`}>
                  Save {annualDiscount}%
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="max-w-5xl mx-auto px-4 mb-6">
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-600">
            {error}
          </div>
        </div>
      )}

      {/* Plan cards */}
      <div className="max-w-6xl mx-auto px-4 pb-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map(plan => {
            const meta = PLAN_META[plan.name] || { color: '#6366F1', cta: 'Choose', description: '' };
            const isHighlight = meta.highlight;
            const isSelecting = selecting === plan.id;

            return (
              <div
                key={plan.id}
                className={`relative flex flex-col rounded-2xl bg-white border transition-all duration-200 hover:-translate-y-1 hover:shadow-xl
                  ${isHighlight
                    ? 'border-[#6366F1] shadow-lg shadow-indigo-100 ring-2 ring-[#6366F1]/20'
                    : 'border-gray-100 shadow-sm'
                  }`}
              >
                {/* Most Popular badge */}
                {meta.badge && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                    <span className="inline-flex items-center gap-1 bg-[#6366F1] text-white text-xs font-bold px-3 py-1 rounded-full shadow-sm">
                      <Star className="h-3 w-3 fill-current" />
                      {meta.badge}
                    </span>
                  </div>
                )}

                <div className="p-6 flex flex-col flex-1">
                  {/* Plan name & price */}
                  <div className="mb-5">
                    <h3
                      className="text-lg font-bold mb-1"
                      style={{ color: meta.color }}
                    >
                      {plan.name}
                    </h3>
                    <p className="text-xs text-slate-400 mb-3">{meta.description}</p>
                    <div className="flex items-end gap-1">
                      <span className="text-4xl font-extrabold text-slate-900">
                        ${cyclePrice(plan.price)}
                      </span>
                      <span className="text-sm text-slate-400 mb-1.5">{cycle === 'yearly' ? '/year' : '/month'}</span>
                    </div>
                    {cycle === 'yearly' && plan.price > 0 && annualDiscount > 0 && (
                      <p className="text-xs text-emerald-600 mt-1">Save {annualDiscount}% vs monthly</p>
                    )}
                  </div>

                  {/* Divider */}
                  <div className="border-t border-gray-50 mb-5" />

                  {/* Features */}
                  <ul className="space-y-2.5 flex-1 mb-6">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-sm text-slate-600">
                        <span
                          className="mt-0.5 h-4 w-4 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${meta.color}18` }}
                        >
                          <Check className="h-2.5 w-2.5" style={{ color: meta.color }} />
                        </span>
                        {feature}
                      </li>
                    ))}
                  </ul>

                  {/* CTA button */}
                  <button
                    onClick={() => handleSelect(plan)}
                    disabled={!!selecting}
                    className={`w-full h-11 rounded-xl text-sm font-semibold transition-all flex items-center justify-center gap-2
                      ${isHighlight
                        ? 'bg-[#6366F1] text-white hover:bg-[#4F46E5] shadow-md shadow-indigo-200'
                        : 'border-2 hover:bg-gray-50'
                      }
                      disabled:opacity-50 disabled:cursor-not-allowed`}
                    style={isHighlight ? {} : { borderColor: meta.color, color: meta.color }}
                  >
                    {isSelecting && <Loader2 className="h-4 w-4 animate-spin" />}
                    {meta.cta}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer note */}
        <p className="text-center text-xs text-slate-400 mt-8">
          Cancel anytime. Prices in USD.
        </p>
      </div>
    </div>
  );
}
